import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { refillPendingCount } from "@/services/refillsApi";
import { Outlet, useLocation } from "react-router-dom";
import { useNavigationStore } from "@/stores/useNavigationStore";
import { AICommandPalette } from "@/components/AICommandPalette";
import { AIAssistantBubble } from "@/components/AIAssistantBubble";
import { MaieLauncherPill } from "@/components/MaieLauncherPill";
import { useTranslation } from "@/hooks/useTranslation";
import { useNotificationStream } from "@/hooks/useNotifications";
import { useCapabilities } from "@/hooks/useCapabilities";
import { Sidebar, Header } from "@/layouts";
import { MaieShellBody } from "@/layouts/MaieShell";
import { ShellChromeProvider } from "@/layouts/shellChrome";
import { useMaieShell } from "@/config/maieShell";
import { buildNav, type SidebarItem } from "@/config/navigation";
import { useClinicShapeStore } from "@/stores/useClinicShapeStore";
import { useNavDiet } from "@/config/navDiet";
import { useAuthStore } from "@/stores/useAuthStore";
import { Permission } from "@/types/user";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RecordingDock } from "@/components/RecordingDock";
import { EncounterArmPanel } from "@/components/medicalRecords/encounter";
import ErrorBoundary from "@/components/ErrorBoundary";
import { NextAppointmentDock } from "@/components/encounter/NextAppointmentDock";
import RecordOnPhoneModal from "@/components/medicalRecords/encounter/RecordOnPhoneModal";
import { RecordingAttestationModal } from "@/components/audioRetention/RecordingAttestationModal";
import { InstallNudgeModal } from "@/components/install/InstallNudgeModal";
import { SynthEventsBridge } from "@/components/encounter/SynthEventsBridge";
import { SynthBackgroundPill } from "@/components/encounter/SynthBackgroundPill";
import { PendingSyncBanner } from "@/components/PendingSyncBanner";
import { startPendingSyncDrainer } from "@/services/pendingSyncDrainer";
import { VoiceprintOnboardingModal } from "@/components/voiceprint/VoiceprintOnboardingModal";
import { useVoiceprintOnboarding } from "@/hooks/useVoiceprintOnboarding";
import { useVoicePrefsSync } from "@/hooks/useVoicePrefsSync";
import { usePdfPrefsSync } from "@/hooks/usePdfPrefsSync";
import { useAccessibilityPrefsSync } from "@/hooks/useAccessibilityPrefsSync";
import { SpecialtyOnboardingModal } from "@/components/profile/SpecialtyOnboardingModal";
import { useSpecialtyOnboarding } from "@/hooks/useSpecialtyOnboarding";
import { useRecordingSessionStore } from "@/stores/useRecordingSessionStore";


/**
 * THE APP'S GLOBAL SURFACES — the ones that are not chrome and not a page.
 *
 * The recorder, the arm panel, the attestation gate, the synth bridge and pill,
 * the offline banner, the assistant launcher, the onboarding modals. None of
 * them belongs to a layout: they are mounted at shell level precisely so a
 * route change never tears them down mid-consult.
 *
 * THEY LIVED INSIDE THE OLD SHELL'S RETURN, and the design shell returns early
 * — so under `?maie=1` the app had pages and chrome but no machinery at all.
 * Pressing "Start visit" created a session that nothing was mounted to render,
 * the attestation gate never appeared, and a doctor mid-consult had no dock.
 * One component, rendered by both branches, is what keeps that from being
 * possible again.
 */
function AppGlobals({
  isFullBleedRoute,
  showCommandPalette,
  setShowCommandPalette,
  showSpecialtyModal,
  setSpecialtyModalClosed,
  showVoiceprintModal,
  setVoiceprintModalClosed,
  isRTL,
}: {
  isFullBleedRoute: boolean;
  showCommandPalette: boolean;
  setShowCommandPalette: (v: boolean) => void;
  showSpecialtyModal: boolean;
  setSpecialtyModalClosed: (v: boolean) => void;
  showVoiceprintModal: boolean;
  setVoiceprintModalClosed: (v: boolean) => void;
  isRTL: boolean;
}) {
  return (
    <>
      {/* Recording dock — voice-first signature surface. Renders as a
          flex-sibling right rail on ≥lg (pushes main content), or a mobile
          bottom sheet. Hidden entirely when no active session.
          When no session is active, NextAppointmentDock takes over the
          same surface (right rail on desktop, slim bottom bar on mobile)
          to surface the imminent appointment with a one-tap Start. The
          two are mutually exclusive in practice — NextAppointmentDock
          returns null when there's no upcoming appointment in the
          ±90 min window, and RecordingDock returns null when there's
          no session. */}
      <RecordingDock />
      {/* Cold-mic arm panel — renders when any entry point calls armSession().
          Returns null otherwise. Fires the real startSession on Start. */}
      <EncounterArmPanel />
      <NextAppointmentDock isRTL={isRTL} />

      {/* Phone-as-mic modal/pill — survives navigation so the doctor can
          minimize to a corner pill while still recording from their
          phone. Driven by usePhoneScribeStore; rendered here at app shell
          level so route changes never tear it down. */}
      <RecordOnPhoneModal />

      {/* Attestation gate — surfaces only when the doctor presses
          start-recording while AI-training retention is on for them.
          Mounted here so every entry point that calls startSession
          (DoctorHome, patient details, timeline, etc.) is covered. */}
      <RecordingAttestationModal />

      {/* First-mobile-web-recording install nudge. Same defer pattern
          as attestation: store sets `awaitingInstallNudge`, this modal
          resolves it. Once-only per browser; desktop/installed contexts
          short-circuit at the gate. */}
      <InstallNudgeModal />

      {/* Background synth — bridges scribe.* socket events into the
          global synth-jobs store and renders a persistent floating pill
          so doctors who minimize the synth overlay (or kick off a regen
          and navigate away) still see progress and get notified when
          their note is ready. */}
      <SynthEventsBridge />
      <SynthBackgroundPill />
      {/* Offline-recordings sync banner — drained by pendingSyncDrainer
          on connection restore, hidden when nothing's queued. Sits
          alongside SynthBackgroundPill so doctors who recorded a stack
          of offline consults see them progress through upload + synth
          when the network comes back. */}
      <PendingSyncBanner />

      {/* AI Components */}
      <AICommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
      />
      <AIAssistantBubble />
      {/* Single bottom-right launcher: encounter mic + chat brain in one
          segmented pill. Replaces the old AIAssistantBubble minimized state
          and the inline FloatingMic that was on DoctorHome. Hides while
          a recording is capturing/paused (the dock owns the corner). */}
      {/* Full-bleed chat surfaces have their own composer exactly where
          the launcher floats - hide it there (the workspace IS the
          assistant; a launcher for it is redundant and overlaps send). */}
      {!isFullBleedRoute && <MaieLauncherPill />}

      {/* First-login specialty picker — gate this BEFORE voiceprint so the
          doctor's profile is set before they record their first session. */}
      <SpecialtyOnboardingModal
        open={showSpecialtyModal}
        onClose={() => setSpecialtyModalClosed(true)}
      />

      {/* First-login voiceprint enrollment */}
      <VoiceprintOnboardingModal
        open={showVoiceprintModal && !showSpecialtyModal}
        onClose={() => setVoiceprintModalClosed(true)}
      />
    </>
  );
}

export const MainLayout: React.FC = () => {
  const location = useLocation();

  // Full-bleed app surfaces own their height + internal scrolling
  // (chat-style pages where a page-level scrollbar is a bug). The messages
  // inbox is a two-pane chat surface just like the assistant — it must own
  // its height so the only scroll is inside the thread/list panes, and the
  // floating launcher pill (which would overlap the composer) stays hidden.
  const isFullBleedRoute =
    location.pathname.startsWith("/assistant") ||
    location.pathname.startsWith("/inbox");

  // No first-run punt here: fresh signups are redirected to /welcome by
  // the register pages themselves, and everything the old onboarding
  // wizard collected lives in Settings + the home Getting-started
  // checklist now.
  // Lock the document (html/body) to the viewport while the app shell is
  // mounted, so the ONLY scroll is the inner <main overflow-auto>. Without
  // this, fixed bottom-anchored elements (the launcher pill, feedback
  // button) combined with an inner scroll container and 100dvh cause Chrome
  // to over-report the root <html> scrollHeight — producing a phantom
  // full-viewport scroll BELOW the shell even though <body> is fully
  // contained at 100dvh. Public/auth routes (login, register) render
  // OUTSIDE MainLayout and keep normal document scroll, so we scope the
  // lock here and restore the prior inline styles on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlHeight: html.style.height,
      htmlOverflow: html.style.overflow,
      bodyHeight: body.style.height,
      bodyOverflow: body.style.overflow,
    };
    html.style.height = "100%";
    html.style.overflow = "hidden";
    body.style.height = "100%";
    body.style.overflow = "hidden";
    return () => {
      html.style.height = prev.htmlHeight;
      html.style.overflow = prev.htmlOverflow;
      body.style.height = prev.bodyHeight;
      body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  const { setActiveView } = useNavigationStore();
  const { t, isRTL } = useTranslation();
  // Desktop sidebar collapse (icon rail) — persisted per-browser so the
  // doctor's layout preference survives reloads. Mobile uses the Sheet
  // drawer and never reads this.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('maie-sidebar-collapsed') === '1',
  );

  // Publish the live desktop sidebar width as a global CSS var so app-root
  // overlays rendered OUTSIDE this layout (the bottom-left FeedbackWidget in
  // Root.route) can clear the sidebar instead of overlaying its "Powered by"
  // foot. Only consumed at the `lg` breakpoint where the sidebar is visible;
  // unmounting on public routes clears it so the widget falls back in place.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-sidebar-width', sidebarCollapsed ? '4rem' : '16rem');
    localStorage.setItem('maie-sidebar-collapsed', sidebarCollapsed ? '1' : '0');
    return () => {
      root.style.removeProperty('--app-sidebar-width');
    };
  }, [sidebarCollapsed]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Cross-device wake-word preference sync. Pulls the user's pref from
  // the server on auth, pushes local toggles back (debounced). Mounted
  // once here so a per-user setting carries between desktop/iPad/phone
  // without each device's localStorage diverging.
  useVoicePrefsSync();
  usePdfPrefsSync();

  // Same pattern for accessibility prefs (text scale, contrast, reduced
  // motion, colour scheme, RTL override). A doctor who picks "Large +
  // dark + high contrast" on desktop sees the same on the iPad.
  useAccessibilityPrefsSync();

  // Hold the first-login modals while ANY recording flow is live or
  // pre-rolling (arm panel up, attestation/install-nudge pending, mic
  // hot). Without this, a fresh owner who starts the /welcome demo and
  // lands here mid-recording would get a modal popped OVER the dock.
  // /welcome itself renders outside MainLayout, so this window is the
  // only exposure. Deliberately NOT gated on demoConsultCompletedAt —
  // that would suppress these forever for demo-skippers; firing after
  // the session commits (while the note is reviewed) is acceptable.
  // Declarative recompute: a status flip simply re-evaluates.
  const recSession = useRecordingSessionStore((s) => s.session);
  const recArmIntent = useRecordingSessionStore((s) => s.armIntent);
  const awaitingAttestation = useRecordingSessionStore(
    (s) => s.awaitingAttestation,
  );
  const awaitingInstallNudge = useRecordingSessionStore(
    (s) => s.awaitingInstallNudge,
  );
  const recordingBusy =
    !!recArmIntent ||
    !!awaitingAttestation ||
    !!awaitingInstallNudge ||
    (!!recSession &&
      (recSession.status === 'capturing' || recSession.status === 'paused'));

  // First-login voiceprint enrollment prompt. Hook decides whether to show;
  // MainLayout renders the modal because it's the post-auth root wrapper.
  const voiceprintPrompt = useVoiceprintOnboarding();
  const [voiceprintModalClosed, setVoiceprintModalClosed] = useState(false);
  const showVoiceprintModal =
    voiceprintPrompt.shouldPrompt && !voiceprintModalClosed && !recordingBusy;

  // First-login specialty picker. Resolves before voiceprint when both fire
  // because specialty drives the prompt + hotword pack — better to know it
  // before the doctor records anything.
  const specialtyPrompt = useSpecialtyOnboarding();
  const [specialtyModalClosed, setSpecialtyModalClosed] = useState(false);
  const showSpecialtyModal =
    specialtyPrompt.shouldPrompt && !specialtyModalClosed && !recordingBusy;

  // Enable real-time notifications via SSE
  useNotificationStream({
    enabled: true,
    showToast: true,
    autoInvalidate: true,
  });

  // Connection-restore drainer for offline-recorded consults. Wires
  // gateway-connect + browser-online listeners that walk completion
  // intents on reconnect and drive each through drain → reprocess in
  // sequence. Idempotent — calling on every render is safe (the
  // drainer guards against duplicate listener wiring). Mounted at
  // the post-auth shell so it only runs for authenticated sessions.
  /* Recording, refill and template reads belong to the roles that hold the
     permission behind them. Asked for by everyone, an accountant's every page
     load fired three 403s at the console. */
  const canRecord = useAuthStore((s) => s.roleCheck.hasPermission(Permission.MEDICAL_RECORDS_READ));
  const canSeeRefills = useAuthStore((s) => s.roleCheck.hasPermission(Permission.PHARMACY_READ));

  useEffect(() => {
    if (!canRecord) return;
    startPendingSyncDrainer({
      copy: {
        syncStarting: isRTL
          ? "بدء معالجة التسجيلات المعلّقة…"
          : "Processing your offline recordings…",
        syncComplete: isRTL
          ? "تمت معالجة جميع التسجيلات"
          : "All recordings processed",
        syncPartial: isRTL
          ? "بعض التسجيلات تحتاج إلى إعادة محاولة"
          : "Some recordings need a retry",
      },
    });
  }, [isRTL, canRecord]);

  // Cold-boot session reconcile. The Zustand-persisted snapshot is a
  // stale view of `recording_sessions`; sync with the server before
  // the dock or any patient page mounts. Two important side effects:
  //   1. Auto-pause-if-orphaned: a row left `capturing` after a refresh
  //      / different tab / killed browser flips to `paused` server-
  //      side, so the doctor sees the truth and resumes deliberately
  //      instead of the dock silently re-acquiring the mic.
  //   2. Terminal-state cleanup: a row that committed/abandoned while
  //      the tab was closed gets dropped from the local snapshot.
  // Disconnect/reconnect within the same page lifetime keeps the
  // sessionStorage tab marker, so the auto-pause branch is skipped —
  // capture continues seamlessly across transient WS blips.
  useEffect(() => {
    if (!canRecord) return;
    void useRecordingSessionStore.getState().rehydrate();
  }, [canRecord]);

  // Refill-pending counter for the Pharmacy sidebar badge. Same query key
  // as Dashboard + PharmacyPage so the three stay in sync through the
  // TanStack cache; `keepPreviousData` avoids the badge flickering off
  // during background refetches.
  const { data: pendingRefills = 0 } = useQuery({
    queryKey: ["refills", "pending-count"],
    queryFn: () => refillPendingCount(true),
    // Refills need pharmacy access; asking without it is a guaranteed 403.
    enabled: canSeeRefills,
  });

  // Sync navigation store with router
  useEffect(() => {
    const pathToView: Record<string, string> = {
      "/assistant": "assistant",
      "/dashboard": "dashboard",
      "/patients": "patients",
      "/schedule": "schedule",
      "/records": "records",
      "/lab-orders": "lab-orders",
      "/pharmacy": "pharmacy",
      "/pharmacy/prescriptions": "pharmacy",
      "/pharmacy/refills": "pharmacy",
      "/pharmacy/formulary": "pharmacy",
      "/pharmacy/lookup": "pharmacy",
      "/bed-management": "bed-management",
      "/census": "census",
      "/adt": "adt",
      "/coding": "coding",
      "/templates": "templates",
      "/protocols": "protocols",
      "/resource-scheduling": "resource-scheduling",
      "/provider-availability": "provider-availability",
      "/waitlist-management": "waitlist-management",
      "/order-management": "order-management",
      "/admin/members": "admin-members",
      "/admin/domains": "admin-domains",
      "/admin/users": "admin-users",
      "/admin/roles": "admin-roles",
      "/admin/system-settings": "admin-system-settings",
      "/admin/system-monitoring": "admin-system-monitoring",
      "/admin/audit-logs": "admin-audit-logs",
      "/admin/feature-toggles": "admin-feature-toggles",
      "/admin/subscription": "admin-subscription",
      "/channels": "channels",
      "/inbox": "inbox",
      "/notifications": "notifications",
      "/settings": "settings",
    };

    const view = pathToView[location.pathname] || "dashboard";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setActiveView(view as any);
  }, [location.pathname, setActiveView]);

  // Keyboard shortcut for command palette (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const { data: _capabilities } = useCapabilities();

  // Clinic size drives which rows the sidebar renders. Refetched per tenant so
  // switching clinics never carries the previous one's shape across.
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  const refreshClinicShape = useClinicShapeStore((s) => s.refresh);
  useEffect(() => {
    if (activeTenantId) void refreshClinicShape(activeTenantId);
  }, [activeTenantId, refreshClinicShape]);

  // Define sidebar structure
  // Sidebar structure lives in src/config/navigation.ts — see the note there
  // on why it is a pure function rather than 330 lines of literal in here.
  // NAV_DIET_ENABLED serves the reworked sidebar; off keeps the previous one,
  // so a clinic can be moved between the two (and back) from Console without
  // a deploy. Per-tenant, like every other platform flag.
  // NAV_DIET_ENABLED serves the reworked sidebar; off keeps the previous one,
  // so a clinic can be moved between the two (and back) from Console without
  // a deploy. Per-tenant, like every other platform flag.
  const { navDiet, resolved: navDietResolved } = useNavDiet();
  const maieShell = useMaieShell();
  const sidebarItems: SidebarItem[] = useMemo(
    // An empty nav for one frame beats the wrong nav for one frame: on a
    // tenant's first load we do not yet know which sidebar they are on, and
    // painting 28 rows that collapse to 11 under the cursor reads as a bug.
    () =>
      navDietResolved ? buildNav({ isRTL, t, pendingRefills, navDiet }) : [],
    [isRTL, t, pendingRefills, navDiet, navDietResolved],
  );

  // `h-screen-safe` = `height: 100vh; height: 100dvh;` — the dynamic
  // viewport unit makes the shell resize as iOS Safari's chrome shows/
  // hides so `<main>` (the inner scroll container below) never extends
  // past the visible viewport. The `100vh` line is the fallback for
  // browsers that don't parse `dvh` (Safari < 15.4 etc.) — without it
  // those clients render the rule as a no-op and the flex column
  // collapses. See index.css `.h-screen-safe`.
  /* THE SHELL IS THE SAME ON EVERY ROUTE, or it is not a shell.
   *
   * Under the design shell, `<Outlet/>` is handed to MaieShell and the old
   * Sidebar/Header are not rendered at all. A page that has been ported draws
   * kit markup and declares its own chrome; one that has not renders exactly as
   * before inside the same frame. What does NOT happen any more is two
   * different shells depending on which route you are standing on. */
  if (maieShell) {
    return (
      <ShellChromeProvider>
        {/* The body has to live INSIDE the provider — it reads the chrome the
            routed page publishes, and a component cannot consume a context it
            is itself providing. */}
        <MaieShellBody
          items={sidebarItems}
          isFullBleed={isFullBleedRoute}
          resetKey={location.pathname}
        />
        {/* The same machinery the old shell mounts — see AppGlobals. Without
            this the design shell is a set of screens with nothing behind the
            buttons: no recorder, no synth bridge, no attestation gate. */}
        <AppGlobals
          isFullBleedRoute={isFullBleedRoute}
          showCommandPalette={showCommandPalette}
          setShowCommandPalette={setShowCommandPalette}
          showSpecialtyModal={showSpecialtyModal}
          setSpecialtyModalClosed={setSpecialtyModalClosed}
          showVoiceprintModal={showVoiceprintModal}
          setVoiceprintModalClosed={setVoiceprintModalClosed}
          isRTL={isRTL}
        />
      </ShellChromeProvider>
    );
  }

  return (
    <div
      className="flex h-screen-safe"
      style={{ backgroundColor: "var(--color-bg-primary)" }}
    >
      {/* Skip-link — first focusable element so keyboard users (and a
          decent share of older clinicians using only the keyboard) can
          jump past the sidebar + header straight to page content.
          Visually hidden until focused; positioned absolutely so it
          doesn't shift layout when revealed. Anchors to <main id="main"
          tabIndex={-1}> below. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{
          backgroundColor: "var(--color-primary-600)",
          color: "var(--color-text-button)",
        }}
      >
        {isRTL ? "تجاوز إلى المحتوى" : "Skip to main content"}
      </a>

      {/* The pre-design shell. Reached only when the design shell is off —
          the branch above returns before this. */}
      <Sidebar
        items={sidebarItems}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <Header
          onMobileMenuOpen={() => setIsMobileMenuOpen(true)}
          onSearchOpen={() => setShowCommandPalette(true)}
        />

        {/* Main Content. tabIndex={-1} lets the skip-link land focus
            here without making <main> a regular tab stop. */}
        <main
          id="main"
          tabIndex={-1}
          className={`flex-1 focus:outline-none ${
            isFullBleedRoute ? "overflow-hidden" : "overflow-auto"
          }`}
        >
          {isFullBleedRoute ? (
            /* Full-bleed app surfaces (the /assistant workspace) own their
               height and internal scrolling — no padding, no breadcrumbs,
               no outer scroll, so the chat column is the ONLY thing that
               scrolls. */
            <div className="h-full">
              <ErrorBoundary resetKey={location.pathname}>
                <Outlet />
              </ErrorBoundary>
            </div>
          ) : (
            <div
              // THE page container. Every routed page renders inside this, so
              // width and gutters are decided in exactly one place. Pages used
              // to bring their own — 16 files carried `max-w-7xl mx-auto px-4
              // sm:px-6 lg:px-8` (double-padding what is already here), seven
              // more used max-w-6xl, and the remaining ~70 had none at all and
              // ran edge to edge. Same product, three different page widths.
              // Anything genuinely narrower (a form, an empty state) sets its
              // own max-width INSIDE this, which still composes.
              className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
              style={{
                // Clearance for the fixed bottom-right MaieLauncherPill (Encounter
                // + Ask MAIE) and the bottom-left feedback widget so a page's last
                // fields/buttons never sit under them on final scroll. The pill
                // sits at bottom-6 (24px) and is up to ~56px tall (~80px total), so
                // 7rem (112px) + the device safe-area inset clears it with a
                // comfortable gap on every page. (The pill dodges any visible
                // bottom dock itself, so we intentionally do NOT add the dock
                // height here — that would over-pad.)
                paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 7rem)",
              }}
            >
              <Breadcrumbs />
              {/* Route-level boundary: keeps the sidebar/header/dock + launcher
                  pill alive when a single page throws. resetKey clears the
                  error on navigation so the doctor can recover by going home. */}
              <ErrorBoundary resetKey={location.pathname}>
                <Outlet />
              </ErrorBoundary>
            </div>
          )}
        </main>
      </div>

      <AppGlobals
        isFullBleedRoute={isFullBleedRoute}
        showCommandPalette={showCommandPalette}
        setShowCommandPalette={setShowCommandPalette}
        showSpecialtyModal={showSpecialtyModal}
        setSpecialtyModalClosed={setSpecialtyModalClosed}
        showVoiceprintModal={showVoiceprintModal}
        setVoiceprintModalClosed={setVoiceprintModalClosed}
        isRTL={isRTL}
      />
    </div>
  );
};
